/**
 * What this user may do — asked of the server that enforces it ([[BUGS #153]]).
 *
 * The app used to mirror `users/{uid}` from Firestore and reconcile the paid
 * plan with an admin grant itself. That was a second implementation of a
 * decision `repositories/entitlements.py` already owns, and the two could read
 * different databases: on staging the admin console and the Stripe webhook
 * write to the staging project while the app's Firebase config resolves to
 * production ([[BUGS #152]]), so a comped Strategist showed as Free.
 *
 * One source now. The server answers; this pushes the answer into the store.
 *
 * `resolved: false` means *we could not find out* — distinct from "free", and
 * the distinction matters: a paid surface that treats them alike shows a
 * paywall to a subscriber ([[BUGS #147]]).
 */
import { getAuth } from 'firebase/auth';

import { gatewayFetch } from './gateway';
import { normalizePlan, useSubscriptionStore, type Plan, type TrialView } from '../stores/subscriptionStore';

export interface Entitlements {
  plan: Plan;
  paidPlan: Plan;
  grant: { plan: Plan; expiresAt: number | null } | null;
  cancelAtPeriodEnd: boolean;
  periodEnd: number | null;
  pendingPlan: Plan | null;
  /** A trial code's trial, running or ended (ROADMAP 0.R R6). */
  trial: TrialView | null;
  /** How the paid plan is billed (ROADMAP 0.R R7). */
  billingInterval: 'month' | 'year' | null;
  resolved: boolean;
}

const UNRESOLVED: Entitlements = {
  plan: 'free', paidPlan: 'free', grant: null,
  cancelAtPeriodEnd: false, periodEnd: null, pendingPlan: null, trial: null, billingInterval: null, resolved: false,
};

/** How long to wait before treating it as unknown. Short: this gates UI. */
const TIMEOUT_MS = 10_000;

const asPendingPlan = (v: unknown): Plan | null =>
  v === 'signals' || v === 'strategist' ? v : null;

export const fetchEntitlements = async (idToken: string): Promise<Entitlements> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await gatewayFetch('user', 'entitlements', { auth: { token: idToken }, signal: controller.signal });
    if (!res.ok) return UNRESOLVED;
    const d = await res.json();
    if (!d?.success) return UNRESOLVED;
    return {
      plan: normalizePlan(d.plan),
      paidPlan: normalizePlan(d.paidPlan),
      grant: d.grant?.plan
        ? {
            plan: normalizePlan(d.grant.plan),
            expiresAt: typeof d.grant.expiresAt === 'number' ? d.grant.expiresAt : null,
          }
        : null,
      cancelAtPeriodEnd: d.cancelAtPeriodEnd === true,
      periodEnd: typeof d.periodEnd === 'number' ? d.periodEnd : null,
      pendingPlan: asPendingPlan(d.pendingPlan),
      trial: asPendingPlan(d.trial?.plan) && typeof d.trial?.endsAt === 'number'
        ? { plan: normalizePlan(d.trial.plan), endsAt: d.trial.endsAt, ended: d.trial.ended === true }
        : null,
      billingInterval: d.billingInterval === 'year' || d.billingInterval === 'month' ? d.billingInterval : null,
      // The server distinguishes "no user doc" (resolved, free) from "the read
      // failed" (unresolved). Anything else that got here is unresolved too.
      resolved: d.resolved === true,
    };
  } catch {
    return UNRESOLVED;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Fetch and apply. Call after sign-in and after anything that can change a
 * plan — checkout return, cancel, resume, a portal round trip.
 *
 * An unresolved answer deliberately leaves the store alone: overwriting a
 * known plan with a guessed `free` is how a subscriber gets asked to pay again.
 */
export const refreshEntitlements = async (): Promise<Entitlements> => {
  // The whole lookup is guarded, not just the fetch. `getAuth()` itself throws
  // when no Firebase app is initialised, and this function promises never to
  // reject — a caller reaching for entitlements must never be the thing that
  // breaks a page.
  let token: string;
  try {
    const user = getAuth().currentUser;
    if (!user) return UNRESOLVED;
    token = await user.getIdToken();
  } catch {
    return UNRESOLVED;
  }
  const ent = await fetchEntitlements(token);
  if (!ent.resolved) return ent;
  const store = useSubscriptionStore.getState();
  store.setEntitlements(ent.paidPlan, ent.grant);
  store.setBillingCycle(ent.cancelAtPeriodEnd, ent.periodEnd, ent.pendingPlan);
  store.setTrial(ent.trial);
  store.setBillingInterval(ent.billingInterval);
  return ent;
};
